<template>
  <transition name="pc-goods-detail" appear>
    <div class="pc-goods-detail" @click="close">
      <button
        type="button"
        class="pc-goods-detail__close"
        :aria-label="goodsDetailCopy.ariaCloseProductDetails"
        @click.stop="close"
      >
        <svg
          width="40"
          height="40"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x="0.5"
            y="0.5"
            width="39"
            height="39"
            rx="7.5"
            fill="black"
            fill-opacity="0.5"
            stroke="#FFF380"
          />
          <path
            d="M10 15L30.5 25M30.5 15L10 25"
            stroke="#FFF380"
            stroke-width="2"
          />
        </svg>
      </button>

      <div class="mobile-marquee mobile-marquee--banner">
        <div class="mobile-marquee-inner">
          <div class="mobile-marquee-track">
            <div
              v-for="dup in 2"
              :key="'mobile-marquee-banner-' + dup"
              class="mobile-marquee-group"
              :aria-hidden="dup === 2 ? 'true' : undefined"
            >
              <img
                v-for="item in 10"
                :key="`mobile-banner-${dup}-${item}`"
                class="mobile-marquee-title"
                src="@/assets/image/index/banner_footer_title.webp"
                width="200"
                height="14"
                alt="Scrolling footer title strip (decorative)"
                aria-hidden="true"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="pc-goods-detail__body">
        <div
          class="pc-goods-detail__backdrop-content"
          @click.stop
          :aria-labelledby="
            currentGoods && currentGoods.title
              ? 'pc-goods-detail-heading-' + activeIndex
              : undefined
          "
        >
          <button
            type="button"
            class="pc-goods-detail__btn pc-goods-detail__btn--prev"
            :disabled="!canPrev"
            :aria-label="goodsDetailCopy.ariaPrevProduct"
            @click.stop="onPrev"
          >
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="20"
                cy="20"
                r="20"
                transform="matrix(-1 0 0 1 40 0)"
                fill="#A6A6A6"
                fill-opacity="0.5"
              />
              <path
                d="M22.3433 25.3137L16.6864 19.6569L22.3433 14"
                stroke="#FFF380"
                stroke-width="2"
              />
            </svg>
          </button>
          <button
            type="button"
            class="pc-goods-detail__btn pc-goods-detail__btn--next"
            :disabled="!canNext"
            :aria-label="goodsDetailCopy.ariaNextProduct"
            @click.stop="onNext"
          >
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="20"
                cy="20"
                r="20"
                fill="#A6A6A6"
                fill-opacity="0.5"
              />
              <path
                d="M17.6567 25.3137L23.3136 19.6569L17.6567 14"
                stroke="#FFF380"
                stroke-width="2"
              />
            </svg>
          </button>

          <Swiper
            v-if="goodsList.length"
            ref="detailSwiper"
            class="pc-goods-detail__swiper"
            :options="swiperOptions"
            @slideChangeTransitionEnd="onSlideChangeTransitionEnd"
          >
            <SwiperSlide
              v-for="(slideItem, idx) in goodsList"
              :key="'goods-detail-slide-' + idx"
              class="pc-goods-detail__slide"
            >
              <div class="pc-goods-detail__panel">
                <div class="pc-goods-detail__gallery-stack">
                  <div
                    class="pc-goods-detail__gallery"
                    :class="{
                      'pc-goods-detail__gallery--multi':
                        normalizeGoodsImages(slideItem).length > 1,
                    }"
                  >
                    <button
                      v-if="normalizeGoodsImages(slideItem).length > 1"
                      type="button"
                      class="pc-goods-detail__gallery-nav pc-goods-detail__gallery-nav--prev"
                      :disabled="!innerImgCanPrev(idx)"
                      :aria-label="goodsDetailCopy.ariaPrevProductImage"
                      @click.stop="onInnerImgPrev(idx)"
                    >
                      <img
                        src="@/assets/image/detail/img_last.webp"
                        alt=""
                        width="40"
                        height="40"
                        decoding="async"
                        role="presentation"
                      />
                    </button>
                    <button
                      v-if="normalizeGoodsImages(slideItem).length > 1"
                      type="button"
                      class="pc-goods-detail__gallery-nav pc-goods-detail__gallery-nav--next"
                      :disabled="!innerImgCanNext(idx)"
                      :aria-label="goodsDetailCopy.ariaNextProductImage"
                      @click.stop="onInnerImgNext(idx)"
                    >
                      <img
                        src="@/assets/image/detail/img_next.webp"
                        alt=""
                        width="40"
                        height="40"
                        decoding="async"
                        role="presentation"
                      />
                    </button>
                    <div class="pc-goods-detail__gallery-viewport">
                      <div
                        class="pc-goods-detail__gallery-track"
                        :style="innerImgTrackStyle(idx)"
                      >
                        <div
                          v-for="(src, imgI) in normalizeGoodsImages(slideItem)"
                          :key="'inner-img-' + idx + '-' + imgI"
                          class="pc-goods-detail__gallery-slide"
                          :style="innerImgSlideCellStyle(slideItem)"
                        >
                          <div
                            v-if="isGalleryImageLoading(idx, imgI, src)"
                            class="pc-goods-detail__gallery-img-loading"
                            aria-hidden="true"
                          >
                            <span class="pc-goods-detail__gallery-img-spinner" />
                          </div>
                          <img
                            class="pc-goods-detail__gallery-img"
                            :class="{ 'is-loading': isGalleryImageLoading(idx, imgI, src) }"
                            :src="src"
                            :alt="
                              (goodsTitlePlain(slideItem) ||
                                goodsDetailCopy.altProductFallback) +
                              ' image ' +
                              (imgI + 1) +
                              goodsDetailCopy.ariaImageMid +
                              normalizeGoodsImages(slideItem).length
                            "
                            decoding="async"
                            @load="onGalleryImageLoad(idx, imgI, src)"
                            @error="onGalleryImageError(idx, imgI, src)"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="normalizeGoodsImages(slideItem).length > 1"
                    class="pc-goods-detail__gallery-dots"
                    role="tablist"
                    :aria-label="
                      goodsTitlePlain(slideItem)
                        ? goodsTitlePlain(slideItem) +
                          goodsDetailCopy.ariaProductImagesSuffix
                        : goodsDetailCopy.ariaProductImagesFallback
                    "
                  >
                    <button
                      v-for="(src, imgI) in normalizeGoodsImages(slideItem)"
                      :key="'inner-dot-' + idx + '-' + imgI"
                      type="button"
                      role="tab"
                      class="pc-goods-detail__gallery-dot"
                      :class="{
                        'pc-goods-detail__gallery-dot--active':
                          imgI === clampedInnerImgIndex(idx),
                      }"
                      :aria-selected="
                        imgI === clampedInnerImgIndex(idx) ? 'true' : 'false'
                      "
                      :aria-label="
                        goodsDetailCopy.ariaImagePrefix +
                        (imgI + 1) +
                        goodsDetailCopy.ariaImageMid +
                        normalizeGoodsImages(slideItem).length
                      "
                      @click.stop="goInnerImg(idx, imgI)"
                    />
                  </div>
                  <div
                    v-else
                    class="pc-goods-detail__gallery-dots--empty"
                  ></div>
                </div>
                <div class="pc-goods-detail__info">
                  <!-- <img
                  class="pc-goods-detail__info-banner"
                  src="@/assets/image/detail/title.webp"
                  alt=""
                  decoding="async"
                  role="presentation"
                /> -->
                  <div
                    :id="'pc-goods-detail-heading-' + idx"
                    class="pc-goods-detail__info-title"
                  >
                    <div v-for="value in slideItem.title">{{ value }}</div>
                  </div>
                  <div class="pc-goods-detail__info-desc">
                    <div v-for="value in slideItem.info">{{ value }}</div>
                  </div>
                  <div class="pc-goods-detail__info-price">
                    <template v-if="slideItem.priceIsNumeric">
                      <span class="pc-goods-detail__info-price-label">{{
                        goodsDetailCopy.priceLabel
                      }}</span>
                      <span>{{ slideItem.price }}円</span>
                      <span class="pc-goods-detail__info-price-label">{{
                        goodsDetailCopy.taxNote
                      }}</span>
                    </template>
                    <span v-else class="pc-goods-detail__info-price-plain">{{
                      slideItem.price
                    }}</span>
                  </div>
                </div>
                <button type="button" class="pc-goods-detail__info-limit">
                  <span>{{ goodsDetailCopy.purchaseLimitPrefix }}</span>
                  {{ goodsDetailCopy.purchaseLimitBody }}
                </button>
              </div>
            </SwiperSlide>
          </Swiper>
        </div>
      </div>

      <!-- <div
        class="pc-goods-detail__marquee pc-goods-detail__marquee--bottom pc-goods-detail__marquee--skin-streaming"
        @click.stop
        aria-hidden="true"
      >
        <div class="pc-goods-detail__marquee-frame">
          <div class="pc-goods-detail__marquee-strip">
            <div class="pc-goods-detail__marquee-track">
              <div
                v-for="dup in 2"
                :key="'pc-goods-detail-streaming-m-' + dup"
                class="pc-goods-detail__marquee-chunk"
                :aria-hidden="dup === 2 ? 'true' : undefined"
              >
                <img
                  v-for="n in 10"
                  :key="`pc-goods-detail-streaming-m-${dup}-${n}`"
                  class="pc-goods-detail__marquee-img"
                  src="@/assets/image/index/about_footer_title.webp"
                  width="314"
                  height="30"
                  alt=""
                  aria-hidden="true"
                  decoding="async"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </div> -->

      <div class="mobile-marquee mobile-marquee--streaming">
        <div class="mobile-marquee-inner">
          <div class="mobile-marquee-track">
            <div
              v-for="dup in 2"
              :key="'mobile-marquee-banner-' + dup"
              class="mobile-marquee-group"
              :aria-hidden="dup === 2 ? 'true' : undefined"
            >
              <img
                v-for="item in 10"
                :key="`mobile-banner-${dup}-${item}`"
                class="mobile-marquee-title"
                src="@/assets/image/index/about_footer_title.webp"
                width="200"
                height="14"
                alt="Scrolling footer title strip (decorative)"
                aria-hidden="true"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { Swiper, SwiperSlide } from "vue-awesome-swiper";
import "swiper/css/swiper.css";
import goodsContent from "@/views/data/goods-content.json";

export default {
  name: "PcGoodsDetailModal",

  components: {
    Swiper,
    SwiperSlide,
  },

  props: {
    goodsList: {
      type: Array,
      default() {
        return [];
      },
    },
    activeIndex: {
      type: Number,
      default: 0,
    },
    canPrev: {
      type: Boolean,
      default: false,
    },
    canNext: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      goodsDetailCopy: goodsContent.goodsDetail,
      swiperSlideSpeed: 420,
      /** 内层配图横向滑动动画时长（ms），与 reduced motion 同步为 0 */
      innerImgSlideMs: 420,
      /** 每个商品在弹层内的当前配图下标（与外层 Swiper 的 slide 下标对应） */
      innerImgIndexBySlide: {},
      /** 详情弹层图片加载状态：key -> loading | loaded | error */
      galleryImgLoadStatus: {},
    };
  },

  computed: {
    currentGoods() {
      const i = this.activeIndex;
      if (i < 0 || i >= this.goodsList.length) return null;
      return this.goodsList[i];
    },
    swiperOptions() {
      const max = Math.max(0, this.goodsList.length - 1);
      const initial = Math.max(
        0,
        Math.min(this.activeIndex < 0 ? 0 : this.activeIndex, max),
      );
      return {
        speed: this.swiperSlideSpeed,
        grabCursor: false,
        simulateTouch: false,
        resistanceRatio: 0.72,
        spaceBetween: 20,
        slidesPerView: 1,
        initialSlide: initial,
        allowTouchMove: false,
      };
    },
  },

  watch: {
    activeIndex(n) {
      this.$nextTick(() => {
        const inst = this.getSwiper();
        if (!inst) return;
        if (inst.activeIndex !== n) {
          inst.slideTo(n, this.swiperSlideSpeed);
        }
      });
    },
    goodsList: {
      immediate: true,
      handler() {
        this.galleryImgLoadStatus = {};
      },
    },
  },

  mounted() {
    if (typeof document !== "undefined") {
      document.documentElement.style.overflow = "hidden";
    }
    if (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      this.swiperSlideSpeed = 0;
      this.innerImgSlideMs = 0;
    }
  },

  beforeDestroy() {
    if (typeof document !== "undefined") {
      document.documentElement.style.overflow = "";
    }
  },

  methods: {
    goodsTitlePlain(item) {
      if (!item || !Array.isArray(item.title)) return "";
      return item.title
        .map((t) => String(t).trim())
        .filter(Boolean)
        .join(" ");
    },
    getSwiper() {
      const c = this.$refs.detailSwiper;
      return c && c.$swiper ? c.$swiper : null;
    },
    close() {
      this.$emit("update:visible", false);
    },

    onSidebarToTop() {
      if (typeof window === "undefined") return;
      const behavior = window.matchMedia("(prefers-reduced-motion: reduce)")
        .matches
        ? "auto"
        : "smooth";
      window.scrollTo({ top: 0, behavior });
    },
    onPrev() {
      if (this.canPrev) this.$emit("prev");
    },
    onNext() {
      if (this.canNext) this.$emit("next");
    },
    onSlideChangeTransitionEnd() {
      const inst = this.getSwiper();
      if (!inst) return;
      const i = inst.activeIndex;
      if (i !== this.activeIndex) {
        this.$emit("change-index", i);
      }
    },

    normalizeGoodsImages(item) {
      if (!item || item.img == null) return [];
      const raw = item.img;
      if (Array.isArray(raw)) return raw.filter(Boolean);
      return [raw].filter(Boolean);
    },
    galleryImageStatusKey(slideIdx, imgI, src) {
      return `${slideIdx}:${imgI}:${src || ""}`;
    },
    isGalleryImageLoading(slideIdx, imgI, src) {
      const key = this.galleryImageStatusKey(slideIdx, imgI, src);
      const status = this.galleryImgLoadStatus[key];
      return !status || status === "loading";
    },
    onGalleryImageLoad(slideIdx, imgI, src) {
      const key = this.galleryImageStatusKey(slideIdx, imgI, src);
      this.$set(this.galleryImgLoadStatus, key, "loaded");
    },
    onGalleryImageError(slideIdx, imgI, src) {
      const key = this.galleryImageStatusKey(slideIdx, imgI, src);
      this.$set(this.galleryImgLoadStatus, key, "error");
    },

    clampedInnerImgIndex(slideIdx) {
      const imgs = this.normalizeGoodsImages(this.goodsList[slideIdx]);
      if (!imgs.length) return 0;
      let stored = this.innerImgIndexBySlide[slideIdx];
      if (typeof stored !== "number" || stored < 0) stored = 0;
      return Math.min(stored, imgs.length - 1);
    },

    innerImgTrackStyle(slideIdx) {
      const imgs = this.normalizeGoodsImages(this.goodsList[slideIdx]);
      const n = imgs.length;
      if (!n) {
        return { display: "none" };
      }
      const i = this.clampedInnerImgIndex(slideIdx);
      const ms = this.innerImgSlideMs;
      return {
        width: n * 100 + "%",
        transform: "translateX(-" + (i * 100) / n + "%)",
        transition:
          ms > 0
            ? "transform " + ms + "ms cubic-bezier(0.4, 0, 0.2, 1)"
            : "none",
      };
    },

    innerImgSlideCellStyle(slideItem) {
      const n = this.normalizeGoodsImages(slideItem).length;
      if (!n) return {};
      return { flex: "0 0 " + 100 / n + "%" };
    },

    goInnerImg(slideIdx, imageIdx) {
      const imgs = this.normalizeGoodsImages(this.goodsList[slideIdx]);
      if (imageIdx < 0 || imageIdx >= imgs.length) return;
      this.$set(this.innerImgIndexBySlide, slideIdx, imageIdx);
    },

    innerImgCanPrev(slideIdx) {
      return this.clampedInnerImgIndex(slideIdx) > 0;
    },

    innerImgCanNext(slideIdx) {
      const imgs = this.normalizeGoodsImages(this.goodsList[slideIdx]);
      return (
        imgs.length > 1 && this.clampedInnerImgIndex(slideIdx) < imgs.length - 1
      );
    },

    onInnerImgPrev(slideIdx) {
      if (!this.innerImgCanPrev(slideIdx)) return;
      const next = this.clampedInnerImgIndex(slideIdx) - 1;
      this.$set(this.innerImgIndexBySlide, slideIdx, next);
    },

    onInnerImgNext(slideIdx) {
      if (!this.innerImgCanNext(slideIdx)) return;
      const next = this.clampedInnerImgIndex(slideIdx) + 1;
      this.$set(this.innerImgIndexBySlide, slideIdx, next);
    },
  },
};
</script>

<style lang="scss" scoped>
/* 弹层内图标按钮：重置 + 过渡；动效分「垂直居中 + scale」与「仅 scale」两种 */
@mixin pc-goods-detail-icon-btn-reset($size: 40px, $z-index: null) {
  margin: 0;
  padding: 0;
  width: $size;
  height: $size;
  border: none;
  background: transparent;
  cursor: pointer;
  line-height: 0;
  appearance: none;
  transform-origin: center center;
  transition: transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1),
    filter 0.2s ease, opacity 0.2s ease;

  @if $z-index != null {
    z-index: $z-index;
  }

  img {
    display: block;
    width: $size;
    height: $size;
    object-fit: contain;
    object-position: center;
    pointer-events: none;
  }
}

@mixin pc-goods-detail-icon-btn-focus {
  &:focus-visible {
    outline: 2px solid #37fff1;
    outline-offset: 3px;
    border-radius: 4px;
  }
}

@mixin pc-goods-detail-icon-motion-y-center {
  transform: translateY(-50%);

  &:hover:not(:disabled) {
    transform: translateY(-50%) scale(1.12);
    filter: brightness(1.14) drop-shadow(0 4px 12px rgba(0, 0, 0, 0.4));
  }

  &:active:not(:disabled) {
    transform: translateY(-50%) scale(0.9);
    filter: brightness(0.92);
    transition-duration: 0.08s;
  }
}

@mixin pc-goods-detail-icon-motion-scale-only {
  transform: none;

  &:hover:not(:disabled) {
    transform: scale(1.1);
    filter: brightness(1.12) drop-shadow(0 3px 10px rgba(0, 0, 0, 0.35));
  }

  &:active:not(:disabled) {
    transform: scale(0.92);
    filter: brightness(0.9);
    transition-duration: 0.08s;
  }
}

@mixin pc-goods-detail-reduce-motion-y-center {
  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &,
    &:hover:not(:disabled),
    &:active:not(:disabled) {
      transform: translateY(-50%);
      filter: none;
    }
  }
}

@mixin pc-goods-detail-reduce-motion-scale {
  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &,
    &:hover:not(:disabled),
    &:active:not(:disabled) {
      transform: none;
      filter: none;
    }
  }
}

.pc-goods-detail {
  position: fixed;
  inset: 0;
  z-index: 200;
  isolation: isolate;
  overflow: auto;

  background-color: rgba(60, 48, 104, 1);
  padding: 28px 0;

  background-image: url("@/assets/image/detail/mobile_page_bg.webp"),
    linear-gradient(to bottom, #9a80ba, #3c3068);
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;

}

.pc-goods-detail__bg {
  display: block;
  width: 90%;
  object-fit: cover;
  object-position: center;
}

/* 顶部 / 底部跑马灯共用骨架；皮肤用 --skin-* 区分背景条图与描边色 */
.pc-goods-detail__marquee {
  position: absolute;
  left: 0;
  width: 100%;
  height: 134px;
  z-index: 2;
  box-sizing: border-box;

  &--top {
    top: 0;
  }

  &--bottom {
    bottom: 0;
    padding-top: 1px;
  }

  &--skin-goods .pc-goods-detail__marquee-frame {
    background-image: url("@/assets/image/index/banner_footer_bg.webp");
  }

  &--skin-streaming .pc-goods-detail__marquee-frame {
    background-image: url("@/assets/image/index/about_footer_bg.webp");
  }

  &--skin-goods .pc-goods-detail__marquee-strip {
    border-top-color: #fff590;
    border-bottom-color: #fff590;
  }

  &--skin-streaming .pc-goods-detail__marquee-strip {
    border-top-color: #d9a2ff;
    border-bottom-color: #d9a2ff;
  }

  img {
    box-sizing: border-box;
    vertical-align: middle;
  }

  .pc-goods-detail__marquee-frame {
    padding-top: 45px;
    position: relative;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
  }

  .pc-goods-detail__marquee-strip {
    overflow: hidden;
    height: 44px;
    display: flex;
    align-items: center;
    background-color: #191616;
    border-top: 1px solid;
    border-bottom: 1px solid;
  }

  .pc-goods-detail__marquee-track {
    display: flex;
    width: max-content;
    flex-shrink: 0;
    align-items: center;
    animation: pc-goods-detail-marquee-track 45s linear infinite;
    will-change: transform;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
      will-change: auto;
    }
  }

  .pc-goods-detail__marquee-chunk {
    display: flex;
    flex-shrink: 0;
    align-items: center;
  }

  .pc-goods-detail__marquee-img {
    flex-shrink: 0;
    margin-right: 30px;
    width: 314px;
    height: 30px;
    object-fit: contain;
    object-position: center;
  }
}

.pc-goods-detail__close {
  position: absolute;
  top: 18px;
  right: 14px;
  // top: 0;
  // right: 0;
  @include pc-goods-detail-icon-btn-reset(40px, 5);
  @include pc-goods-detail-icon-btn-focus;
  @include pc-goods-detail-icon-motion-scale-only;
  @include pc-goods-detail-reduce-motion-scale;
}

.pc-goods-detail__body {
  display: flex;
  align-items: center;
  justify-content: center;
}

.pc-goods-detail__backdrop-content {
  position: relative;
  // position: absolute;
  // // top: 50%;
  // top: 90px;
  // left: 50%;
  // z-index: 3;
  // transform: translateX(-50%);
  // // width: 1200px;
  max-width: calc(100% - 40px);
  // pointer-events: auto;

  .pc-goods-detail__btn {
    position: absolute;
    top: 50%;
    @include pc-goods-detail-icon-btn-reset(40px, 3);
    @include pc-goods-detail-icon-btn-focus;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.35;
    }

    &--prev,
    &--next {
      @include pc-goods-detail-icon-motion-y-center;
      @include pc-goods-detail-reduce-motion-y-center;
    }

    &--prev {
      left: -20px;
    }

    &--next {
      right: -20px;
    }
  }

  .pc-goods-detail__panel {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    // padding: 50px;
    padding: 40px 20px;
    box-sizing: border-box;
    background-color: rgba(0, 0, 0, 0.5);
    border-radius: 8px;
    color: #fff;

    .pc-goods-detail__gallery-stack {
      display: flex;
      flex-direction: column;
      align-items: center;
      // width: 360px;
      flex-shrink: 0;
    }

    .pc-goods-detail__gallery {
      position: relative;
      padding: 16px;
      width: 360px;
      height: 360px;
      box-sizing: border-box;
      object-fit: contain;
      object-position: center;
      background-color: #191616;
      border-radius: 8px;
      border: 1px solid #fff380;

      &--multi .pc-goods-detail__gallery-viewport {
        height: 100%;
      }

      &-nav {
        position: absolute;
        top: 50%;
        @include pc-goods-detail-icon-btn-reset(30px, 2);
        @include pc-goods-detail-icon-btn-focus;
        @include pc-goods-detail-icon-motion-y-center;
        @include pc-goods-detail-reduce-motion-y-center;

        &--prev {
          left: 4px;
        }

        &--next {
          right: 4px;
        }

        &:disabled {
          cursor: not-allowed;
          opacity: 0.35;
        }
      }

      &-viewport {
        position: relative;
        width: 100%;
        height: 100%;
        overflow: hidden;
      }

      &-track {
        display: flex;
        height: 100%;
        min-height: 0;
        will-change: transform;
      }

      &-slide {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        min-width: 0;
        min-height: 0;
        box-sizing: border-box;
      }

      &-img-loading {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(
          180deg,
          rgba(25, 22, 22, 0.7) 0%,
          rgba(25, 22, 22, 0.42) 100%
        );
        z-index: 1;
        pointer-events: none;
      }

      &-img-spinner {
        width: 24px;
        height: 24px;
        border: 3px solid rgba(255, 243, 128, 0.3);
        border-top-color: #fff380;
        border-radius: 50%;
        animation: mobile-goods-detail-img-spin 0.9s linear infinite;
      }

      /* 铺满 slide，由 object-fit: contain 按父级宽高等比缩放，长边贴边、整图可见并居中 */
      &-img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
        object-position: center;
        transition: opacity 0.2s ease;

        &.is-loading {
          opacity: 0;
        }
      }
    }

    .pc-goods-detail__gallery-dots {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
      gap: 10px;
      // margin-top: 6px;
      padding: 0;
      width: 100%;
      // min-height: 28px;
      height: 30px;
      box-sizing: border-box;

      &--empty {
        height: 30px;
      }
    }

    .pc-goods-detail__gallery-dot {
      flex-shrink: 0;
      margin: 0;
      padding: 0;
      width: 6px;
      height: 6px;
      border: none;
      border-radius: 50%;
      background-color: #898989;
      cursor: pointer;
      appearance: none;
      transition: background-color 0.2s ease, transform 0.2s ease;

      &:hover {
        transform: scale(1.15);
      }

      &--active {
        background-color: #fff380;
      }

      &:focus-visible {
        outline: 2px solid #37fff1;
        outline-offset: 2px;
      }
    }

    .pc-goods-detail__info {
      // margin-left: 47px;
      width: 100%;
      margin-bottom: 18px;
      padding: 0 26px;
      text-align: left;

      &-banner {
        // margin: 0 0 60px 50px;
        width: 498px;
        object-fit: contain;
        object-position: center;
      }

      &-title {
        // margin: 20px 0 20px;
        color: #fff380;
        font-size: 16px;
        font-weight: 700;
        margin-bottom: 10px;
      }

      &-desc {
        max-height: 140px;
        overflow: auto;
        margin-bottom: 18px;
        color: #fff;
        font-size: 12px;
        font-weight: 600;
        font-size: 12px;
        line-height: 150%;
      }

      &-price {
        // margin: 40px 0 20px;
        font-weight: 700;
        font-size: 24px;
        text-align: left;
        font-style: Bold;
        color: #fff;
        line-height: 150%;
        letter-spacing: 0%;

        &-label {
          margin-right: 14px;
          font-size: 14px;
        }
      }

      &-price-plain {
        display: block;
        width: 100%;
        font-size: 18px;
        font-weight: 700;
        line-height: 1.45;
        color: #fff380;
        word-break: break-word;
        overflow-wrap: anywhere;
      }

      &-limit {
        // margin: 20px 0 30px;
        padding: 6px;
        width: 100%;
        color: #ffed9d;
        text-align: center;
        border: 1px solid #fff380;
        border-radius: 8px;
        cursor: default;
        font-family: "Noto Sans JP", "Noto Sans", sans-serif;
        font-weight: 600;
        font-style: SemiBold;
        font-size: 14px;
        line-height: 150%;
        letter-spacing: 0%;

        span {
          color: #fff;
        }
      }
    }
  }
}

.pc-goods-detail__swiper {
  width: 100%;
  // min-height: 3.5em;

  &::v-deep .swiper-container {
    overflow: hidden;
    width: 100%;
  }

  &::v-deep .swiper-wrapper {
    align-items: center;
  }

  &::v-deep .swiper-slide {
    box-sizing: border-box;
  }
}

.pc-goods-detail__slide {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.pc-goods-detail-enter-active,
.pc-goods-detail-leave-active {
  transition: opacity 0.32s ease;
}

.pc-goods-detail-enter,
.pc-goods-detail-leave-to {
  opacity: 0;
}

@keyframes mobile-goods-detail-img-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .pc-goods-detail__gallery-dot {
    transition: none;

    &:hover {
      transform: none;
    }
  }
}

/* 无限滚动横幅 */
.mobile-marquee {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 60px;
  background-size: 100% 100%;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;

  &--banner {
    background-image: url("@/assets/image/index/banner_footer_bg.webp");
  }
  &--banner &-inner {
    border-top: 2px solid #fff590;
    border-bottom: 2px solid #fff590;
  }

  &--streaming {
    background-image: url("@/assets/image/index/about_footer_bg.webp");
  }

  &--streaming &-inner {
    border-top: 2px solid #d9a2ff;
    border-bottom: 2px solid #d9a2ff;
  }

  &-inner {
    width: 100%;
    height: 24px;
    overflow: hidden;
    background-color: #191616;
    display: flex;
    align-items: center;
  }

  &-track {
    display: flex;
    width: max-content;
    flex-shrink: 0;
    align-items: center;
    animation: mobile-marquee 30s linear infinite;
    will-change: transform;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
      will-change: auto;
    }
  }

  &-group {
    display: flex;
    flex-shrink: 0;
    align-items: center;
  }

  &-title {
    flex-shrink: 0;
    margin-right: 20px;
    width: 200px;
    height: 10px;
    object-fit: contain;
    object-position: center;
  }
}

/* 无限滚动动画 */
@keyframes mobile-marquee {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}
</style>

<style lang="scss">
@keyframes pc-goods-detail-marquee-track {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}
</style>
